import { BranchSummaryItem } from '../hooks/useBrandQueries';

export interface MetricDefinition {
  id: string;
  label: string;
  unit: 'currency' | 'percentage' | 'number' | 'score';
  category: 'financial' | 'customer' | 'performance';
}

export interface BestBranchMetric {
  branchId: number;
  branchName: string;
  value: number;
  currency: string;
}

export const BRANCH_COMPARISON_METRICS: readonly MetricDefinition[] = [
  { id: 'revenue', label: 'مجموع فروش', unit: 'currency', category: 'financial' },
  { id: 'invoiceCount', label: 'مجموع تعداد سفارش', unit: 'number', category: 'financial' },
  { id: 'satisfactionScore', label: 'امتیاز رضایت', unit: 'percentage', category: 'performance' },
  { id: 'subscription', label: 'اشتراک‌گیری', unit: 'percentage', category: 'performance' },
  { id: 'creditInvoiceCount', label: 'مجموع تعداد سفارش‌های اعتباری', unit: 'number', category: 'financial' },
  { id: 'creditInvoiceAmount', label: 'مجموع مبلغ سفارش‌های اعتباری', unit: 'currency', category: 'financial' },
  { id: 'usedCredit', label: 'مجموع مبلغ اعتبارات مصرفی', unit: 'currency', category: 'financial' },
];

export const getNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return 0;
};

export const getMetricValue = (branch: BranchSummaryItem, metricId: string): number => {
  switch (metricId) {
    case 'revenue':
      return getNumber(branch.currentMonthRevenue);
    case 'invoiceCount':
      return getNumber(branch.invoiceCount);
    case 'customerCount':
      return getNumber(branch.customerCount);
    case 'satisfactionScore':
      return getNumber(branch.satisfactionScore);
    case 'subscription':
      return getNumber(branch.subscription ?? branch.subscriptionRate ?? branch.subscription_percentage);
    case 'creditInvoiceCount':
      return getNumber(branch.creditInvoiceCount ?? branch.validCreditInvoiceCount);
    case 'creditInvoiceAmount':
      return getNumber(branch.creditInvoiceAmount ?? branch.validCreditInvoiceAmount);
    case 'usedCredit':
      return getNumber(branch.usedCredit ?? branch.usedCreditAmount ?? branch.sumUsedCredit);
    case 'newCustomers':
      return getNumber(branch.newCustomers);
    case 'returningCustomers':
      return getNumber(branch.returningCustomers);
    case 'lostCustomers':
      return getNumber(branch.lostCustomers);
    default:
      return 0;
  }
};

export const formatMetricValue = (value: number, unit: string, currency = 'تومان'): string => {
  const normalized = value.toLocaleString('fa-IR');
  switch (unit) {
    case 'currency':
      return `${normalized} ${currency}`;
    case 'percentage':
      return `${normalized}%`;
    case 'score':
      return `${value.toLocaleString('fa-IR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/۱۰۰`;
    default:
      return normalized;
  }
};

export const findBestBranch = (
  branches: BranchSummaryItem[],
  metricId: string
): BestBranchMetric | null => {
  if (!branches.length) return null;

  let best: BestBranchMetric | null = null;

  for (const branch of branches) {
    const value = getMetricValue(branch, metricId);
    if (!best || value > best.value) {
      best = {
        branchId: branch.branchId,
        branchName: branch.branchName,
        value,
        currency: branch.currency
      };
    }
  }

  return best;
};

export const getKpiVariant = (
  category: MetricDefinition['category']
): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' => {
  switch (category) {
    case 'financial':
      return 'primary';
    case 'performance':
      return 'success';
    case 'customer':
      return 'info';
    default:
      return 'default';
  }
};
